<template>
  <div
    :class="dropdownClasses"
    role="combobox"
    tabindex="0"
    :aria-label="`${label}: ${currentOptionLabel}`"
    :aria-expanded="isOpened.toString()"
    aria-haspopup="listbox"
    @click.prevent="onToggle"
    @keydown="onToggleKeydown"
  >
    <div v-if="label" class="trbo-a-dropdown__label">{{ label }}:</div>
    <div
      v-if="currentOptionLabel"
      class="trbo-a-dropdown__current-value"
    >{{ currentOptionLabel }}</div>
    <div class="trbo-a-dropdown__arrow">
      <vertical-arrow />
    </div>
    <div v-if="isOpened" class="trbo-a-dropdown__options" role="listbox">
      <div
        v-for="(item, index) of options"
        ref="dropdownOptions"
        :key="index"
        role="option"
        tabindex="0"
        :aria-selected="value === item.value"
        :data-text="item.label"
        :class="optionItemClasses(item)"
        @click.prevent="onSelect(item)"
        @keydown="event => onSelectKeydown(event, item)"
      >
        {{ item.label }}
      </div>
    </div>
  </div>
</template>

<script>
import VerticalArrow from './VerticalArrow.vue'

export default {
  components: {
    VerticalArrow
  },
  props: {
    label: {
      type: String,
      default: ''
    },
    value: {
      type: String,
      default: null
    },
    options: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      isOpened: false
    }
  },
  computed: {
    dropdownClasses () {
      return {
        'trbo-a-dropdown': true,
        'trbo-a-dropdown--is-opened': this.isOpened
      }
    },
    currentOptionLabel () {
      if (!this.value) return null
      const item = this.options.find(o => o.value === this.value)
      if (!item) return null
      return item.label
    }
  },
  watch: {
    isOpened (value) {
      this.$nextTick(() => {
        if (value) {
          window.addEventListener('click', this.onContainerClick)
        } else {
          window.removeEventListener('click', this.onContainerClick)
        }
      })
    }
  },
  destroyed () {
    window.removeEventListener('click', this.onContainerClick)
  },
  methods: {
    onToggle () {
      this.isOpened = !this.isOpened
    },
    onToggleKeydown (event) {
      if (event.key === 'Enter' || event.key === ' ') {
        this.onToggle()
        event.preventDefault()
      }

      if (this.isOpened && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
        this.arrowNavigation(event.key)
        event.preventDefault()
      }

      if (event.key === 'Escape') {
        this.isOpened = false
        event.stopPropagation()
      }
    },
    onSelect (item) {
      this.isOpened = false
      this.$emit('select', item.value)
    },
    onSelectKeydown (event, item) {
      if (event.key === 'Enter' || event.key === ' ') {
        this.onSelect(item)
        event.preventDefault()
        event.stopPropagation()
      }

      if (event.key === 'Escape') {
        this.isOpened = false
        event.stopPropagation()
      }
    },
    onContainerClick (event) {
      if (!event.target.closest('.trbo-a-dropdown')) {
        this.isOpened = false
      }
    },
    optionItemClasses (item) {
      return {
        'trbo-a-dropdown__options__item': true,
        'trbo-a-dropdown__options__item--is-selected': this.value === item.value
      }
    },
    arrowNavigation (key) {
      const dropdownOptionsSize = this.$refs.dropdownOptions.length
      if (dropdownOptionsSize === 0) return

      const focusedDropdownOption = this.$refs.dropdownOptions.find((element) => {
        return document.activeElement === element
      })

      if (focusedDropdownOption) {
        const focusedIndex = this.$refs.dropdownOptions.findIndex((element) => element === focusedDropdownOption)
        let nextIndex = key === 'ArrowDown' ? focusedIndex + 1 : focusedIndex - 1
        if (nextIndex < 0) nextIndex = dropdownOptionsSize - 1
        else if (nextIndex >= dropdownOptionsSize) nextIndex = 0
        this.$refs.dropdownOptions[nextIndex]?.focus()
      } else {
        this.$refs.dropdownOptions[0]?.focus()
      }
    }
  }
}
</script>

<style scoped src="./dropdown.scss" />
